import React from "react";

interface CareScoreBreakdown {
  serviceScore?: number;
  contentScore?: number;
  runwayScore?: number;
  contactScore?: number;
  growthScore?: number;
  planningScore?: number;
  velocityScore?: number;
}

interface CareScoreBadgeProps {
  score: number;
  size?: "sm" | "md";
  showTooltip?: boolean;
  breakdown?: CareScoreBreakdown;
}

/**
 * Displays a client care score as a colored circular badge.
 * Green for 80+, yellow for 60-79, red for below 60.
 * Hovering shows a tooltip with the score breakdown when provided.
 */
export const CareScoreBadge: React.FC<CareScoreBadgeProps> = ({
  score,
  size = "sm",
  showTooltip = true,
  breakdown,
}) => {
  const getColor = (s: number) => {
    if (s >= 80)
      return { bg: "bg-green-100", text: "text-green-700", ring: "ring-green-200" };
    if (s >= 60)
      return { bg: "bg-yellow-100", text: "text-yellow-700", ring: "ring-yellow-200" };
    return { bg: "bg-red-100", text: "text-red-700", ring: "ring-red-200" };
  };

  const color = getColor(score);
  const sizeClasses = size === "sm" ? "w-8 h-8 text-xs" : "w-10 h-10 text-sm";

  const tooltipLines = breakdown
    ? [
        `Service: ${breakdown.serviceScore ?? "-"}`,
        `Content: ${breakdown.contentScore ?? breakdown.velocityScore ?? "-"}`,
        ...(breakdown.runwayScore != null ? [`Runway: ${breakdown.runwayScore}`] : []),
        `Contact: ${breakdown.contactScore ?? "-"}`,
        ...(breakdown.growthScore != null ? [`Growth: ${breakdown.growthScore}`] : []),
        ...(breakdown.planningScore != null ? [`Planning: ${breakdown.planningScore}`] : []),
      ]
    : [];

  const nativeTitle =
    !showTooltip && tooltipLines.length > 0
      ? tooltipLines.join(" | ")
      : undefined;

  return (
    <div className="relative group">
      <div
        title={nativeTitle}
        className={`inline-flex items-center justify-center rounded-full font-semibold ring-1 ${sizeClasses} ${color.bg} ${color.text} ${color.ring}`}
      >
        {score}
      </div>
      {showTooltip && tooltipLines.length > 0 && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
          {tooltipLines.map((line, i) => (
            <div key={i}>{line}</div>
          ))}
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
        </div>
      )}
    </div>
  );
};
